import type { PeerId } from '../types';

/**
 * BLE advertisement local names often truncate `Lynx|<userId>`. Treat a match when
 * the canonical id starts with the scan (or the strings are equal).
 */
export function peerIdMatches(storedId: PeerId, scannedId: PeerId): boolean {
  if (storedId === scannedId) {
    return true;
  }
  if (storedId.length === 0 || scannedId.length === 0) {
    return false;
  }
  return storedId.startsWith(scannedId) || scannedId.startsWith(storedId);
}

/**
 * Picks the best-known peer id for RSSI state. BLE may truncate localName (iOS);
 * prefer the longest matching id already in the peer map.
 *
 * A truncated id that fits two different people stays as it is: iOS cut Lexie to `134` at the
 * Greek (2026-10-11), and three characters are a guess, not an identity. Guessing wrong would
 * put one friend's Bluetooth range on another friend's dot.
 */
export function resolveCanonicalPeerId(scanned: PeerId, knownPeerIds: Iterable<PeerId>): PeerId {
  let best: PeerId | null = null;
  for (const id of knownPeerIds) {
    if (!peerIdMatches(id, scanned)) {
      continue;
    }
    const longer = id.length >= scanned.length ? id : scanned;
    if (best === null || longer.startsWith(best)) {
      best = longer;
    } else if (!best.startsWith(longer)) {
      return scanned;
    }
  }
  return best ?? scanned;
}

/** Peer ids to remove when a longer canonical id supersedes a truncated duplicate. */
export function findShorterDuplicatePeerIds(
  canonicalId: PeerId,
  knownPeerIds: Iterable<PeerId>,
): PeerId[] {
  const remove: PeerId[] = [];
  for (const oldId of knownPeerIds) {
    if (oldId === canonicalId) {
      continue;
    }
    if (peerIdMatches(oldId, canonicalId) && oldId.length < canonicalId.length) {
      remove.push(oldId);
    }
  }
  return remove;
}

/**
 * Prefer userId from advertisement local name (`Lynx|<id>`) or service data for our UUID.
 * Port of Lynx BleManager.parseUserIdFromScan.
 */
export function parsePeerIdFromAdvertisement(
  localName: string | undefined,
  serviceData: Record<string, DataView | undefined> | undefined,
  serviceUuid: string,
  localNamePrefix: string,
): PeerId | undefined {
  if (localName?.startsWith(localNamePrefix)) {
    const id = localName.slice(localNamePrefix.length).trim();
    if (id.length > 0) {
      return id;
    }
  }
  if (!serviceData) {
    return undefined;
  }
  const candidates = [serviceUuid, serviceUuid.toLowerCase(), serviceUuid.toUpperCase()];
  for (const key of candidates) {
    const dv = serviceData[key];
    if (dv && dv.byteLength > 0) {
      try {
        const text = new TextDecoder()
          .decode(new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength))
          .replace(/\0/g, '')
          .trim();
        if (text.length > 0) {
          return text;
        }
      } catch {
        /* ignore */
      }
    }
  }
  return undefined;
}

/**
 * The shortest Bluetooth id worth placing. iOS cut Lexie to `134` at the Greek (2026-10-11); at a
 * festival full of Prii phones three characters fit strangers too, so anything shorter is dropped.
 */
export const MIN_BLE_PEER_ID_LENGTH = 8;

/**
 * The roster user an id belongs to, or null when it is nobody we watch.
 *
 * The roster is the subscriber's full user ids (lynx_event_peers). Every key the engine files a
 * person under - a full id, a cut-short Bluetooth id, a GPS-over-Bluetooth id - resolves through
 * this, so one friend is one entry however many ways they reached us (2026-10-14 Turnstile: Olivia
 * twice, "2 of 1 sharing"). A prefix fits only when it is long enough and names exactly one person.
 */
export function resolveRosterPeerId(id: PeerId, roster: readonly PeerId[]): PeerId | null {
  const needle = id.trim().toLowerCase();
  if (needle.length === 0) {
    return null;
  }
  let match: PeerId | null = null;
  for (const full of roster) {
    const hay = full.toLowerCase();
    if (hay === needle) {
      return full;
    }
    if (needle.length >= MIN_BLE_PEER_ID_LENGTH && hay.startsWith(needle)) {
      if (match !== null && match !== full) {
        return null;
      }
      match = full;
    }
  }
  return match;
}
